import { Injectable } from '@angular/core';
import { EngineService } from '../engine.service';
import { Util } from './util.service';
import { Axis, Matrix, PivotTools, Quaternion, Space, TransformNode, Vector3, Viewport } from '@babylonjs/core';
import { Point } from '@root/app.interfaces';
import { FitModelError } from '@core/errors/fit-model-error';

@Injectable({
	providedIn: 'root',
})
export class ModelFitterService {
	private engineService: EngineService;
	private canvas: HTMLCanvasElement;
	scaleDirection = { positive: 1, negative: 0 };

	constructor(private util: Util) {}

	setContext(context: EngineService, canvas: HTMLCanvasElement): void {
		this.engineService = context;
		this.canvas = canvas;
	}

	fitModelToQuad(rawCoords: Point[]): void {
		try {
			const coords = this.util.sortQuad(rawCoords);

			this.engineService.scene.updateTransformMatrix(true); // had a case where transform matrix was stale (maybe some camera changes had happensed??) and getProjectedCorners was returning incorrect values

			const canvasDiag = Math.hypot(this.canvas.width, this.canvas.height);

			const quadDiag = Math.hypot(
				Math.max(...coords.map((p) => p.x)) - Math.min(...coords.map((p) => p.x)),
				Math.max(...coords.map((p) => p.y)) - Math.min(...coords.map((p) => p.y)),
			);

			this.engineService.blindOpeningDataMap.set(this.engineService.blindId, { quadDiag, canvasDiag });

			const norm = quadDiag / canvasDiag;

			if (!this.engineService.isMobile) {
				const radius = Math.max(
					this.engineService.camera.lowerRadiusLimit,
					Math.min(this.util.radiusFromNormDesktop(norm), this.engineService.camera.upperRadiusLimit),
				);
				this.engineService.camera.radius = radius;
			} else if (this.engineService.isMobile) {
				const radius = Math.max(
					this.engineService.camera.lowerRadiusLimit,
					Math.min(this.util.radiusFromNormMobile(norm), this.engineService.camera.upperRadiusLimit),
				);
				this.engineService.camera.radius = radius;
			}
			this.engineService.scene.updateTransformMatrix(true);

			// convert the coordinates to babylon world space
			const viewportSize = this.getViewportSizeAtDepth();
			const babCoords = this.util.scaleToBabylonSpace(
				coords,
				this.canvas.width,
				this.canvas.height,
				viewportSize.width,
				viewportSize.height,
			);

			//position blind in the middle of the quad
			const QuadMid = this.util.getQuadCenter(babCoords[0], babCoords[1], babCoords[2], babCoords[3]);
			this.engineService.boundingBox.position = new Vector3(QuadMid.x, QuadMid.y, QuadMid.z);

			// get inclinations
			const leftInclination = this.util.calculateAngleBetweenLines(coords[0], coords[3], { x: 0, y: 0 }, { x: 1000, y: 0 }); // taken from the bottom left inside (-> + <180°)
			const rightInclination = this.util.calculateAngleBetweenLines(coords[1], coords[2], { x: 0, y: 0 }, { x: 1000, y: 0 });

			const topInclination = this.util.calculateAngleBetweenLines(coords[0], coords[1], { x: 0, y: 0 }, { x: 0, y: 1000 }); // taken from the bottom left inside (↑ + <180°)
			const bottomInclination = this.util.calculateAngleBetweenLines(coords[3], coords[2], { x: 0, y: 0 }, { x: 0, y: 1000 });

			// set rotation directions
			const clockwiseYRotation = topInclination - bottomInclination > 0 ? true : topInclination - bottomInclination < 0 ? false : null;

			const isClockwise =
				(rightInclination > 90 && leftInclination > 90) || (rightInclination > 90 && 180 - rightInclination < leftInclination);

			const isCounterclockwise =
				(leftInclination < 90 && rightInclination < 90) || (leftInclination < 90 && leftInclination < 180 - rightInclination);

			const clockwiseZRotation = isClockwise ? true : isCounterclockwise ? false : null;

			// correct Z rotation for if it's outside the reasonable bounds of what can be corrected with the yz (CTB) rotation algorithm
			if (
				clockwiseZRotation !== null &&
				((topInclination > 90 && bottomInclination > 90) || (topInclination < 90 && bottomInclination < 90))
			) {
				// rotate on Z-axis until top and bottom diff converge under the tolerance (assuming z -> yz -> x flow)
				this.optimiseRotationCTB(coords, Axis.Z, clockwiseZRotation, topInclination, bottomInclination);
			}

			this.adjustYscaling(babCoords); // adjust height of the model so it fits the quad // important to do here as the distance from centre affects inclination angle due to camera distorsion

			// rotate the model on Y-axis so the angle difference between the top and bottom is the same and in the same direction so that the top and bottom can be aligned by rotating on Z-axis
			this.optimiseRotationCTB(coords, Axis.Y, clockwiseYRotation, topInclination, bottomInclination);

			this.finalZRotation(coords, topInclination); // rotate on Z-axis until top (and be extension bottom) sides are aligned

			this.adjustXscaling(babCoords); // adjust width of model to fit quad

			// rotate the model on X-axis so the diffs between the model and quad are the same
			this.optimiseRotationCLR(coords, Axis.X, leftInclination, rightInclination);
			this.adjustYscaling(babCoords); // re-adjust the height of the model as the X rotation has probably made it visually too short

			const boundingBoxSettings = {
				position: this.engineService.boundingBox.position.clone(),
				scaling: this.engineService.boundingBox.scaling.clone(),
				rotationQuaternion: this.engineService.boundingBox.rotationQuaternion.clone(),
				cameraRadius: this.engineService.camera.radius,
				isEnabled: true,
			};

			this.engineService.modelFitBBoxSettings[this.engineService.blindId] = boundingBoxSettings;
			this.engineService.boundingBoxesSettings[this.engineService.blindId] = boundingBoxSettings;
		} catch (e) {
			// if fitting fails, resets model to default position
			const coordsString = '(' + rawCoords.map((p) => `${p.x}, ${p.y}`).join(', ') + ')';
			let errorInfo: string | Error;
			if (e instanceof Error && (e.message === 'Maximum CTB iterations reached!' || e.message === 'Maximum CLR reversals reached!')) {
				errorInfo = 'Error: ' + e.message;
			} else {
				errorInfo = e;
			}

			console.warn(
				'Unable to fit model to quad with coordinates:',
				coordsString,
				'\n',
				'Canvas size: ',
				this.canvas.width,
				this.canvas.height,
				'\n',
				errorInfo,
			);

			this.engineService.resetBoundingBoxDisplay(this.engineService.blindId, true);
			throw new FitModelError(e instanceof Error ? e.message : String(e), rawCoords);
		}
	}

	getViewportSizeAtDepth(): { width: number; height: number } {
		const fov = this.engineService.camera.fov; // get the field of view (FOV) in radians
		const aspectRatio = this.engineService.scene.getEngine().getAspectRatio(this.engineService.camera);

		const height = 2 * Math.tan(fov / 2) * this.engineService.camera.radius; // calculate the viewport height and width at the radius depth
		const width = height * aspectRatio;

		return { width, height };
	}

	// TODO: combine optimiseRotationCLR, optimiseRotationCTB and maybe finalZRotation into core & wrapper functions
	// optimise rotation to Converge Left and Right angle difference (CLR)
	optimiseRotationCLR(coords: Array<object>, axis: Vector3, leftInclination: number, rightInclination: number): void {
		const MAX_REVERSALS = 5; // maximum number of reversals before stopping the loop // could make more if still having CLR issues
		let modelCorners = this.getProjectedCorners(); // get corners of the blind as the user sees them on screen space

		const tolerance = 0.015; // convergence tolerance // arbitrary picked number 0.859° +50% on CTB
		let stepFactor = 0.01; // determines magnitude size scaling // arbitrary
		const maxStep = 0.1; // largest step size (5.73°)
		const minStep = 0.0001; // smallest step size for fine-tuning // 0.00573° (20.63″)

		let leftDiff = this.util.calculateAngleBetweenLines(coords[0], coords[3], modelCorners[1], modelCorners[2]); // difference in angle of the corresponding side of the model and quad
		let rightDiff = this.util.calculateAngleBetweenLines(coords[1], coords[2], modelCorners[0], modelCorners[3]);
		let leftModelInclination = this.util.calculateAngleBetweenLines(modelCorners[1], modelCorners[2], { x: 0, y: 0 }, { x: 1000, y: 0 }); // inclination of the left side of the model as measure from the bottom left
		let rightModelInclination = this.util.calculateAngleBetweenLines(modelCorners[0], modelCorners[3], { x: 0, y: 0 }, { x: 1000, y: 0 }); // inclination of the right side of the model as measure from the bottom left

		// tests which rotation direction is needed
		let tempRotationQuaternion = Quaternion.RotationAxis(axis, minStep); // creates a small backwards (X-Axis) rotation
		tempRotationQuaternion = this.engineService.boundingBox.rotationQuaternion.multiply(tempRotationQuaternion); // creates a copy of the models rotation with the test backwards rotation applied
		const testCorners = this.getProjectedCorners(tempRotationQuaternion); // gets the theoretical corner of the model with the test backwards rotation applied
		const newLeftDiff = this.util.calculateAngleBetweenLines(coords[0], coords[3], testCorners[1], testCorners[2]); // gets the the diffs of the modesl sides with the test backwards rotation applied
		const newRightDiff = this.util.calculateAngleBetweenLines(coords[1], coords[2], testCorners[0], testCorners[3]);
		const forwardsXRotation = leftDiff >= rightDiff ? newLeftDiff > leftDiff : newRightDiff > rightDiff; // works out if the test backwards rotation has made the diffs larger. If it has then the rotation is set to forwards. This is checked for the side with the biggest disparity (diff) as this is the side we want to get closer.

		let error = Math.abs(leftDiff - rightDiff); // difference between top and bottom diff
		let total = leftDiff + rightDiff; // total combined error of top and bottom diff

		let previousError = error; // used to store the error from the last iteration // set to 500 as the theoretical maximum is 179° + 179° = 358° then add some extra for safety
		let previousTotal = total; // used to store the total from the last iteration // set to 500 as the theoretical maximum is 179° + 179° = 358° then add some extra for safety
		let reverse = false; // used to reverse direction of rotation if optimal convergence is passed
		let reversalCount = 0; // used to count how many times the rotation direction has been reversed
		let leftRelIncPositive = leftModelInclination >= leftInclination; // used to store the relative inclinations which are uesed to tell if a models side has changed relative inclination (crossed a quad side)
		let rightRelIncPositive = rightModelInclination >= rightInclination;
		let stage = 1; // used to track the stage of the loop // 1 = grace period, not in the right orientation for optimisation but heading in the right direction, 2 = optimisation, 3 = reversal
		let previousLeftDiff = leftDiff;
		let previousRightDiff = rightDiff;

		let loop = true; // to satisfy linter
		while (loop) {
			const magnitude = error;
			let step = Math.max(minStep, Math.min(maxStep, magnitude * stepFactor)); // sets rotation step based on magnitude of error and clamps it within min and max step range

			step = forwardsXRotation ? -step : step; // sets the correct direction of rotation
			step = reverse ? -step : step; // reverses the direction of rotation if rotation has proceeded to far for correct convergence

			this.engineService.boundingBox.rotate(axis, step, Space.LOCAL);
			this.engineService.boundingBox.computeWorldMatrix(true); // updates boundingBox data
			modelCorners = this.getProjectedCorners(); // gets new corner coordinates after rotation
			leftDiff = this.util.calculateAngleBetweenLines(coords[0], coords[3], modelCorners[1], modelCorners[2]); // gets difference in angle of the corresponding side of the model and quad
			rightDiff = this.util.calculateAngleBetweenLines(coords[1], coords[2], modelCorners[0], modelCorners[3]);
			leftModelInclination = this.util.calculateAngleBetweenLines(modelCorners[1], modelCorners[2], { x: 0, y: 0 }, { x: 1000, y: 0 });
			rightModelInclination = this.util.calculateAngleBetweenLines(modelCorners[0], modelCorners[3], { x: 0, y: 0 }, { x: 1000, y: 0 });

			error = Math.abs(leftDiff - rightDiff); // update errors
			total = leftDiff + rightDiff;

			const isCorrectOrentation = // checks if the model is in the correct orientation for convergence
				(leftDiff < previousLeftDiff && rightDiff > previousRightDiff) || (leftDiff > previousLeftDiff && rightDiff < previousRightDiff);

			const relativeInclinationChange = // checks if a model side has changed relative inclination (crossed a quad side)
				leftRelIncPositive !== leftModelInclination >= leftInclination || rightRelIncPositive !== rightModelInclination >= rightInclination;

			if (isCorrectOrentation || (stage === 1 && relativeInclinationChange)) {
				// checks if the loop is in the correct stage
				stage = 2; // sets the stage to optimisation
			} else if (stage === 3) {
				stage = 1; // sets the stage to grace period after reversal
			} else if (stage !== 1) {
				stage = 3; // sets the stage to reversal
			}

			if (
				// conditions to tell if convergence has been reached
				(previousError < tolerance && total > previousTotal && error > previousError && stage === 2) ||
				(previousError === 0 && previousTotal === 0)
			) {
				this.engineService.boundingBox.rotate(axis, -step, Space.LOCAL); // undo the last step as it was too far
				this.engineService.boundingBox.computeWorldMatrix(true);
				modelCorners = this.getProjectedCorners(); // not sure why this is here anymore, possibly to update something in the engine??
				loop = false;
				break;
			}

			if (stage === 3) {
				// reveses the direction of rotation if the model has passed the optimal convergence point
				reverse = !reverse;
				reversalCount++;
				if (reversalCount === 3) {
					stepFactor = 0.001;
				}
				if (reversalCount > MAX_REVERSALS) {
					// stops the loop if the rotation direction has been reversed too many times (stops infinite/extremely long loop)
					throw new Error('Maximum CLR reversals reached!');
				}
			}

			previousError = error; // store the errors from the last iteration
			previousTotal = total;
			previousLeftDiff = leftDiff; // store the diffs from the last iteration
			previousRightDiff = rightDiff;
			leftRelIncPositive = leftModelInclination >= leftInclination; // store the relative inclinations from the last iteration
			rightRelIncPositive = rightModelInclination >= rightInclination;
		}
	}

	// optimise rotation to Converge Top and Bottom angle difference (CTB)
	optimiseRotationCTB(
		coords: Array<object>,
		axis: Vector3,
		clockwiseRotation: boolean,
		topInclination: number,
		bottomInclination: number,
	): void {
		const MAX_ITERATIONS = 1500; // maximum number of iterations to prevent infinite loop // arbitrary

		// Probably doesn't make heaps of difference having this here but should increase accuracy // It is reset by setGizmoModelMeshScaling
		if (this.engineService.shutter.modelsValue[this.engineService.blindId] === 1) {
			this.engineService.getTopSceneHandler(0.5, false);
		}

		let modelCorners = this.getProjectedCorners(); // get corners of the blind as the user sees them on screen space

		const tolerance = 0.01; // convergence tolerance // arbitrary picked number 0.573°
		let stepFactor = 0.01; // determines magnitude size scaling // arbitrary
		const maxStep = 0.1; // largest step size (5.73°)
		const minStep = 0.0001; // smallest step size for fine-tuning // 0.00573° (20.63″)

		let topDiff = this.util.calculateAngleBetweenLines(coords[0], coords[1], modelCorners[1], modelCorners[0]); // difference between the inclination of the blind top/bottom edge and the corresponding quad sides
		let bottomDiff = this.util.calculateAngleBetweenLines(coords[3], coords[2], modelCorners[2], modelCorners[3]);
		let topModelInclination = this.util.calculateAngleBetweenLines(modelCorners[1], modelCorners[0], { x: 0, y: 0 }, { x: 0, y: 1000 }); // inclination of the top of the model as measure from the bottom left inside
		let bottomModelInclination = this.util.calculateAngleBetweenLines(modelCorners[2], modelCorners[3], { x: 0, y: 0 }, { x: 0, y: 1000 }); // inclination of the bottom of the model as measure from the bottom left inside

		let error = Math.abs(bottomDiff - topDiff); // difference between top and bottom diff
		let total = bottomDiff + topDiff; // total combined error of top and bottom diff

		let optimise = false; // used to stop convergence on a local minima
		let reverse = false; // used to reverse direction of rotation if optimal convergence is passed
		let smaller; // used to store which diff was smaller when direction of rotation was reversed
		let count = 0; // iteration counter to prevent infinite loop

		optimise = this.setOptimise(
			optimise,
			axis,
			clockwiseRotation,
			topModelInclination,
			topInclination,
			bottomModelInclination,
			bottomInclination,
		); // checks if the loop should begin optimising for convergence straight away

		while ((!optimise || error > tolerance) && count < MAX_ITERATIONS) {
			// rotation loop // condition for if optimsing for convergence should begin and once it has condition for convergence being found
			const magnitude = optimise ? error : total; // to speed up convergence we use total error, which is usually higher, before optimisation has begun. Once optimisation has begun we use relative error which is usually smaller and better for fine tuning
			let step = Math.max(minStep, Math.min(maxStep, magnitude * stepFactor)); // sets rotation step based on magnitude of error and clamps it within min and max step range

			if ((!clockwiseRotation && axis === Axis.Y) || (clockwiseRotation && axis === Axis.Z)) {
				// sets the correct direction of rotation
				step = -step;
			}
			step = reverse ? -step : step; // reverses the direction of rotation if rotation has proceeded to far for correct convergence

			this.engineService.boundingBox.rotate(axis, step, Space.LOCAL);
			this.engineService.boundingBox.computeWorldMatrix(true); // updates boundingBox data
			modelCorners = this.getProjectedCorners(); // gets new corner coordinates after rotation
			bottomDiff = this.util.calculateAngleBetweenLines(coords[3], coords[2], modelCorners[2], modelCorners[3]); // gets difference in angle of the corresponding side of the model and quad
			topDiff = this.util.calculateAngleBetweenLines(coords[0], coords[1], modelCorners[1], modelCorners[0]);
			topModelInclination = this.util.calculateAngleBetweenLines(modelCorners[1], modelCorners[0], { x: 0, y: 0 }, { x: 0, y: 1000 }); // gets the inclinations of the model
			bottomModelInclination = this.util.calculateAngleBetweenLines(modelCorners[2], modelCorners[3], { x: 0, y: 0 }, { x: 0, y: 1000 });

			optimise = this.setOptimise(
				optimise,
				axis,
				clockwiseRotation,
				topModelInclination,
				topInclination,
				bottomModelInclination,
				bottomInclination,
			); // check if conditions are met to being convergence
			if (!reverse) {
				const result = this.setReverseRotationTrue(
					axis,
					clockwiseRotation,
					topModelInclination,
					topInclination,
					bottomModelInclination,
					bottomInclination,
				); // checks if the direction of rotation needs to be reversed
				reverse = result.reverse; // sets the reverse variable
				smaller = result.smaller; // sets the smaller variable
			} else {
				reverse = this.setReverseRotationFalse(smaller, topDiff, bottomDiff); // checks if the direction of rotation needs to be un-reversed
			}

			error = Math.abs(bottomDiff - topDiff); // updates errors
			total = topDiff + bottomDiff;

			count++;
			stepFactor = count === 500 ? stepFactor / 2 : stepFactor;
		}

		if (count >= MAX_ITERATIONS) {
			// if the maximum number of iterations has been reached then throw an error
			throw new Error('Maximum CTB iterations reached!');
		}
	}

	// conditions for if convergence optimisation should begin
	setOptimise(
		optimise: boolean,
		axis: Vector3,
		clockwiseRotation: boolean,
		topModelInclination: number,
		topInclination: number,
		bottomModelInclination: number,
		bottomInclination: number,
	): boolean {
		if (axis !== Axis.Z && axis !== Axis.Y) {
			return optimise;
		} // Keep existing state if invalid axis

		let condition = false;
		// different conditions for axis of rotation and rotation direction
		if (axis === Axis.Z) {
			condition = clockwiseRotation
				? topModelInclination <= topInclination || bottomModelInclination <= bottomInclination
				: topModelInclination >= topInclination || bottomModelInclination >= bottomInclination;
		} else if (axis === Axis.Y) {
			condition = clockwiseRotation
				? topModelInclination >= topInclination || bottomModelInclination <= bottomInclination
				: topModelInclination <= topInclination || bottomModelInclination >= bottomInclination;
		}

		return optimise || condition; // keeps true state // once optimisation has begun it will not stop until convergence is reached
	}

	// conditions for if the rotation direction should be reversed
	setReverseRotationTrue(
		axis: Vector3,
		clockwiseRotation: boolean,
		topModelInclination: number,
		topInclination: number,
		bottomModelInclination: number,
		bottomInclination: number,
	): { reverse: boolean; smaller: string } {
		let reverse = null;

		// different conditions for axis of rotation and rotation direction
		if (axis === Axis.Z) {
			if (clockwiseRotation && topModelInclination < topInclination && bottomModelInclination < bottomInclination) {
				// both sides of the model have passed the quad sides
				reverse = true;
			} else if (!clockwiseRotation && topModelInclination > topInclination && bottomModelInclination > bottomInclination) {
				reverse = true;
			} else {
				reverse = false;
			}
		} else if (axis === Axis.Y) {
			if (clockwiseRotation && topModelInclination > topInclination && bottomModelInclination < bottomInclination) {
				// both sides of the model have passed the quad sides
				reverse = true;
			} else if (!clockwiseRotation && topModelInclination < topInclination && bottomModelInclination > bottomInclination) {
				reverse = true;
			} else {
				reverse = false;
			}
		}

		let smaller = null;
		// if the rotation direction has been reversed then store the samller diff at the time (needed to un-reverse the rotation direction)
		if (reverse) {
			if (Math.abs(topModelInclination - topInclination) < Math.abs(bottomModelInclination - bottomInclination)) {
				smaller = 'top';
			} else {
				smaller = 'bottom';
			}
		}

		return { reverse, smaller };
	}

	// conditions for if the rotation direction should be un-reversed
	setReverseRotationFalse(smaller: string, topDiff: number, bottomDiff: number): boolean {
		let reverse;

		// checks if the smaller diff has become equal to or greater than the larger diff
		if (smaller === 'top') {
			if (topDiff >= bottomDiff) {
				reverse = false;
			} else {
				reverse = true;
			}
		} else if (smaller === 'bottom') {
			if (bottomDiff >= topDiff) {
				reverse = false;
			} else {
				reverse = true;
			}
		}

		return reverse;
	}

	// rotate the model on z axis to align top and bottom
	finalZRotation(coords: Array<object>, topInclination: number): void {
		let modelCorners = this.getProjectedCorners(); // get corners of the blind as the user sees them on screen space

		// determine z axis rotation direction for yz fine tuning
		const topModelInclination = this.util.calculateAngleBetweenLines(modelCorners[1], modelCorners[0], { x: 0, y: 0 }, { x: 0, y: 1000 });
		const clockwiseZRotation = topModelInclination > topInclination;

		let topDiff = this.util.calculateAngleBetweenLines(coords[0], coords[1], modelCorners[1], modelCorners[0]);

		while (topDiff > 0.1) {
			// TODO: may need to change and recheck this tolerance and make step adaptive
			if (clockwiseZRotation) {
				this.engineService.boundingBox.rotate(Axis.Z, -0.001, Space.LOCAL);
			} else {
				this.engineService.boundingBox.rotate(Axis.Z, 0.001, Space.LOCAL);
			}

			this.engineService.boundingBox.computeWorldMatrix(true);
			modelCorners = this.getProjectedCorners();
			topDiff = this.util.calculateAngleBetweenLines(coords[0], coords[1], modelCorners[1], modelCorners[0]);
		}
	}

	adjustYscaling(babCoords: Array<object>): void {
		// calculate the difference in height between the model and quad and adjust accordingly

		// Have to pull this as if the blind is fully closed some invisible geometry makes the bottomMidPoint too low // It is reset by setGizmoModelMeshScaling
		if (this.engineService.shutter.modelsValue[this.engineService.blindId] === 1) {
			this.engineService.getTopSceneHandler(0.5, false);
		}

		let modelBabCorners = this.getModelBabCorners(this.engineService.boundingBox.rotationQuaternion); // gets the corner for the model in babylon world space
		let topMidPoint = this.util.getMidpoint(modelBabCorners[0], modelBabCorners[1]); // half way along the top of the model
		let bottomMidPoint = this.util.getMidpoint(modelBabCorners[2], modelBabCorners[3]); // half way along the bottom of the model
		let targetPoint = this.util.getPlaneLineIntersection(
			babCoords[0],
			babCoords[1],
			this.engineService.camera.position,
			topMidPoint,
			bottomMidPoint,
		); // gets the intersection between the line drawn through the middle of the model and a plane drawn from the camera to the top side of the quad so that model appears visually the right height.
		const topHeightDiff = this.util.getLineLength(targetPoint, topMidPoint);
		this.scaleFromOneSide(targetPoint.y < topMidPoint.y ? -topHeightDiff : topHeightDiff, Axis.Y, this.scaleDirection.positive);

		modelBabCorners = this.getModelBabCorners(this.engineService.boundingBox.rotationQuaternion); // gets the corner for the model in babylon world space
		topMidPoint = this.util.getMidpoint(modelBabCorners[0], modelBabCorners[1]); // half way along the top of the model
		bottomMidPoint = this.util.getMidpoint(modelBabCorners[2], modelBabCorners[3]); // half way along the bottom of the model
		targetPoint = this.util.getPlaneLineIntersection(
			babCoords[3],
			babCoords[2],
			this.engineService.camera.position,
			topMidPoint,
			bottomMidPoint,
		); // gets the intersection between the line drawn through the middle of the model and a plane drawn from the camera to the bottom side of the quad so that model appears visually the right height.
		const bottomHeightDiff = this.util.getLineLength(targetPoint, bottomMidPoint);
		this.scaleFromOneSide(targetPoint.y > bottomMidPoint.y ? -bottomHeightDiff : bottomHeightDiff, Axis.Y, this.scaleDirection.negative);

		this.engineService.setGizmoModelMeshScaling(true);
	}

	adjustXscaling(babCoords: Array<{ x; y; z }>): void {
		// calculate the difference in width between the model and quad and adjust accordingly
		let modelBabCorners = this.getModelBabCorners(this.engineService.boundingBox.rotationQuaternion);
		let midPointRight = this.util.getMidpoint(modelBabCorners[0], modelBabCorners[3]); // mid point is used as this is on the axis the model is rotated around
		let midPointLeft = this.util.getMidpoint(modelBabCorners[1], modelBabCorners[2]);
		let targetPoint = this.util.getPlaneLineIntersection(
			babCoords[0],
			babCoords[3],
			this.engineService.camera.position,
			midPointRight,
			midPointLeft,
		); // gets the intersection between the line drawn through the middle of the model and a plane drawn from the camera to the left side of the quad so that model appears visually the right width.
		// const leftDistanceDiff = this.util.getLineLength(midPointLeft, targetPoint); // legacy but useful for debug comparison
		let offset = midPointLeft.x > midPointRight.x ? 0.05 : 0; // anecdotally this helps the far side align better. may need to remove with more testing

		let rightDir = this.engineService.boundingBox.getDirection(Vector3.Right()).normalize(); // unit vector
		let delta = Vector3.FromArray([targetPoint.x, targetPoint.y, targetPoint.z]).subtract(
			Vector3.FromArray([midPointLeft.x, midPointLeft.y, midPointLeft.z]),
		);
		let signedDist = Vector3.Dot(delta, rightDir);
		let ammout = signedDist > 0 ? -signedDist : -signedDist + offset;

		this.scaleFromOneSide(ammout, Axis.X, this.scaleDirection.positive);

		modelBabCorners = this.getModelBabCorners(this.engineService.boundingBox.rotationQuaternion);
		midPointRight = this.util.getMidpoint(modelBabCorners[0], modelBabCorners[3]);
		midPointLeft = this.util.getMidpoint(modelBabCorners[1], modelBabCorners[2]);
		targetPoint = this.util.getPlaneLineIntersection(
			babCoords[1],
			babCoords[2],
			this.engineService.camera.position,
			midPointRight,
			midPointLeft,
		); // gets the intersection between the line drawn through the middle of the model and a plane drawn from the camera to the right side of the quad so that model appears visually the right width.
		// const rightDistanceDiff = this.util.getLineLength(midPointRight, targetPoint); // legacy but useful for debug comparison
		offset = midPointRight.x > midPointLeft.x ? 0.05 : 0; // anecdotally this helps the far side align better. may need to remove with more testing

		rightDir = this.engineService.boundingBox.getDirection(Vector3.Right()).normalize(); // unit vector
		delta = Vector3.FromArray([targetPoint.x, targetPoint.y, targetPoint.z]).subtract(
			Vector3.FromArray([midPointRight.x, midPointRight.y, midPointRight.z]),
		);
		signedDist = Vector3.Dot(delta, rightDir);
		ammout = signedDist > 0 ? signedDist + offset : signedDist;

		this.scaleFromOneSide(ammout, Axis.X, this.scaleDirection.negative);

		this.engineService.setGizmoModelMeshScaling(true);
	}

	// mimics the scaling perfromed by the gizmo
	scaleFromOneSide(distance: number, scaleAxis: Vector3, direction: number): void {
		const originalParent = this.engineService.boundingBox.parent;
		const anchor = new TransformNode('anchor'); // setting up anchor
		anchor.scaling = new Vector3(1, 1, 1);
		anchor.rotationQuaternion = Quaternion.RotationYawPitchRoll(anchor.rotation.y, anchor.rotation.x, anchor.rotation.z);
		anchor.rotationQuaternion.copyFrom(this.engineService.boundingBox.rotationQuaternion);
		anchor.position.copyFrom(this.engineService.boundingBox.absolutePosition);

		const modelCorners = this.getModelBabCorners(this.engineService.boundingBox.rotationQuaternion); // model corners in babylon world space

		PivotTools._RemoveAndStorePivotPoint(this.engineService.boundingBox);
		const deltaScale = new Vector3(1, 1, 1);
		if (scaleAxis === Axis.X) {
			const targetScale = this.engineService.boundingBox.scaling.x + distance; // the x scale value we are targeting
			deltaScale.x = targetScale / this.engineService.boundingBox.scaling.x; // the actual needed change in scale adhereing to parent.scaling * child.scaling = targetScale
			if (direction === this.scaleDirection.positive) {
				// set the point to scale from according to direction
				const midPointRight = this.util.getMidpoint(modelCorners[0], modelCorners[3]);
				anchor.position = new Vector3(midPointRight.x, midPointRight.y, midPointRight.z);
			} else if (direction === this.scaleDirection.negative) {
				const midPointLeft = this.util.getMidpoint(modelCorners[1], modelCorners[2]);
				anchor.position = new Vector3(midPointLeft.x, midPointLeft.y, midPointLeft.z);
			}
		} else if (scaleAxis === Axis.Y) {
			const targetScale = this.engineService.boundingBox.scaling.y + distance; // the y scale value we are targeting
			deltaScale.y = targetScale / this.engineService.boundingBox.scaling.y; // the actual needed change in scale adhereing to parent.scaling * child.scaling = targetScale
			if (direction === this.scaleDirection.positive) {
				// set the point to scale from according to direction
				const midPoint = this.util.getMidpoint(modelCorners[2], modelCorners[3]);
				anchor.position = new Vector3(midPoint.x, midPoint.y, midPoint.z);
			} else if (direction === this.scaleDirection.negative) {
				const midPoint = this.util.getMidpoint(modelCorners[1], modelCorners[0]);
				anchor.position = new Vector3(midPoint.x, midPoint.y, midPoint.z);
			}
		}

		anchor.addChild(this.engineService.boundingBox);
		anchor.scaling = deltaScale;
		anchor.removeChild(this.engineService.boundingBox);
		this.engineService.boundingBox.setParent(originalParent);
		PivotTools._RestorePivotPoint(this.engineService.boundingBox);

		this.engineService.boundingBox.computeWorldMatrix(true);
		this.engineService.getCurrentRootMesh().computeWorldMatrix(true);
	}

	getProjectedCorners(rotationQuaternion: Quaternion = this.engineService.boundingBox.rotationQuaternion): Vector3[] {
		// gets the corners of the model in screen space (as the user sees it)
		const corners = this.getModelBabCorners(rotationQuaternion); // model corners in babylon world space
		const projectedCorners = [];
		const viewport = new Viewport(0, 0, this.canvas.width, this.canvas.height);

		for (let i = 0; i < corners.length; i++) {
			const projectedCorner = Vector3.Project(
				// projects the corners position to screen space (as the user see it)
				corners[i],
				Matrix.Identity(),
				this.engineService.scene.getTransformMatrix(),
				viewport,
			);
			projectedCorners.push(projectedCorner);
		}

		return projectedCorners;
	}

	// works by getting the local extremes (not technically but for all intents and purposes) of the model and constructing the points of a rectangle from them then applying the model's current rotation to the rectangle points to get the model's world corner coordinates
	getModelBabCorners(rotationQuaternion: Quaternion): Vector3[] {
		// gets the corners of the model in babylon world space
		const { localMin, localMax } = this.getDimensions(); // get the local extremes

		const localCorners = [
			// construct the points of a rectangle
			new Vector3(localMax.x, localMax.y, this.engineService.boundingBox.getAbsolutePivotPoint().z),
			new Vector3(localMin.x, localMax.y, this.engineService.boundingBox.getAbsolutePivotPoint().z),
			new Vector3(localMin.x, localMin.y, this.engineService.boundingBox.getAbsolutePivotPoint().z),
			new Vector3(localMax.x, localMin.y, this.engineService.boundingBox.getAbsolutePivotPoint().z),
		];

		const rotatedCorners = [];

		for (let i = 0; i < localCorners.length; i++) {
			// rotate rectangle points by model current rotation (or whatever rotation is passed)
			const temp = new Vector3();
			localCorners[i].rotateByQuaternionAroundPointToRef(rotationQuaternion, this.engineService.boundingBox.getAbsolutePivotPoint(), temp);
			rotatedCorners.push(temp);
		}

		return rotatedCorners;
	}

	// gets the dimensions of the model by setting its rotation to zero then getting the model's extremes and then re-applying the original rotation
	getDimensions(): { localMin: Vector3; localMax: Vector3 } {
		// gets the local extremes of the model
		const currentRootMesh = this.engineService.getCurrentRootMesh();
		const saveQuaternion = this.engineService.boundingBox.rotationQuaternion || this.engineService.boundingBox.rotation.toQuaternion();
		this.engineService.boundingBox.rotationQuaternion = Quaternion.Zero();
		this.engineService.boundingBox.computeWorldMatrix(true);
		currentRootMesh.computeWorldMatrix(true);

		const boundingVectors = currentRootMesh.getHierarchyBoundingVectors();
		const localMin = boundingVectors.min;
		const localMax = boundingVectors.max;

		this.engineService.boundingBox.rotationQuaternion = saveQuaternion;
		this.engineService.boundingBox.computeWorldMatrix(true);
		currentRootMesh.computeWorldMatrix(true);

		return { localMin, localMax };
	}

	// For testing
	testOverlay: HTMLCanvasElement | null = null;
	overlayCtx: CanvasRenderingContext2D | null = null;

	testFittingAlgoStatic(coords: Point[]): void {
		if (!this.testOverlay) {
			this.testOverlay = document.createElement('canvas');
			this.testOverlay.id = 'testOverlay';
			this.testOverlay.style.position = 'absolute';
			this.testOverlay.style.pointerEvents = 'none';
			this.testOverlay.style.zIndex = '10';

			this.canvas.parentElement?.appendChild(this.testOverlay);
		}

		this.testOverlay.width = this.canvas.width;
		this.testOverlay.height = this.canvas.height;
		this.testOverlay.style.top = this.canvas.offsetTop + 'px';
		this.testOverlay.style.left = this.canvas.offsetLeft + 'px';

		this.overlayCtx = this.testOverlay.getContext('2d');

		this.overlayCtx.clearRect(0, 0, this.testOverlay.width, this.testOverlay.height);
		this.overlayCtx.strokeStyle = 'red';
		this.overlayCtx.lineWidth = 2;

		this.overlayCtx.beginPath();
		this.overlayCtx.moveTo(coords[0].x, coords[0].y);
		this.overlayCtx.lineTo(coords[1].x, coords[1].y);
		this.overlayCtx.lineTo(coords[2].x, coords[2].y);
		this.overlayCtx.lineTo(coords[3].x, coords[3].y);
		this.overlayCtx.closePath();
		this.overlayCtx.stroke();

		this.fitModelToQuad(coords);
	}
}
